import React, {
    useCallback, useEffect, useMemo, useState,
} from 'react';

import { useJsApiLoader } from '@react-google-maps/api';

import StationFilterChips from './StationFilterChips';
import StationMapPane from './StationMapPane';
import createEvents from './events';
import { smoothPanTo } from './mapUtils';
import { setMapsAuthFailed, useMapsAuthFailed } from './mapsAuthState';

import styles from './StationMapCore.scss';

import type { TrackEvent } from './events';
import type { MarkerProps } from '@react-google-maps/api';

export type MapMode = 'depart' | 'arrive';

export type Position = MarkerProps['position'];

export interface SearchParams {
    departDate: string;
    departTripId?: string;
    destinationPlaceId?: string;
    destinationText: string;
    originPlaceId?: string;
    originText: string;
    returnDate?: string;
}

export interface SelectedStation {
    name: string;
    id: string;
    position: Position;
    side: MapMode;
}

export interface StationMapCoreProps {
    arriveLocation: Position;
    arriveStationCount: number;
    departLocation: Position;
    departStationCount: number;
    googleMapsApiKey: string;
    isArrivalStationsFiltered: boolean;
    isDepartureStationsFiltered: boolean;
    mapContainerStyle?: React.CSSProperties;
    onClose?: () => void;
    resetArrivalStations: () => void;
    resetDepartureStations: () => void;
    searchParams: SearchParams;
    setArrivalStationOnlyActive: (stationId: string) => void;
    setDepartureStationOnlyActive: (stationId: string) => void;
    showCityMarker?: boolean;
    totalTripsUnfiltered?: number;
    trackEvent: TrackEvent;
    trips: Trip[];
}

export interface Trip {
    arriveId: string;
    arriveLatLong: Position;
    arriveName: string;
    carrierCode?: string;
    departId: string;
    departLatLong: Position;
    departName: string;
    duration?: number;
    tripId: string;
}

function StationMapCore({
    arriveLocation,
    arriveStationCount,
    departLocation,
    departStationCount,
    googleMapsApiKey,
    isArrivalStationsFiltered,
    isDepartureStationsFiltered,
    onClose,
    resetArrivalStations,
    resetDepartureStations,
    searchParams,
    setArrivalStationOnlyActive,
    setDepartureStationOnlyActive,
    totalTripsUnfiltered,
    trackEvent,
    trips,
}: StationMapCoreProps) {
    const [station, setStation] = useState<SelectedStation | undefined>();

    const events = useMemo(
        () => createEvents(trackEvent),
        [trackEvent],
    );

    const { isLoaded, loadError } = useJsApiLoader({
        googleMapsApiKey,
        id: 'google-map-script',
    });

    const authFailed = useMapsAuthFailed();
    useEffect(() => {
        if (authFailed) return undefined;
        const w = window as Window & { gm_authFailure?: () => void }; // eslint-disable-line camelcase
        w.gm_authFailure = () => setMapsAuthFailed(); // eslint-disable-line camelcase
        return undefined;
    }, [authFailed]);

    const hasFilters = isArrivalStationsFiltered || isDepartureStationsFiltered;

    const baseProperties = useMemo(() => ({
        arrive_address: searchParams.destinationText,
        arrive_place_id: searchParams.destinationPlaceId,
        arrive_station_count: arriveStationCount,
        depart_address: searchParams.originText,
        depart_place_id: searchParams.originPlaceId,
        depart_station_count: departStationCount,
        device_form_factor: window.innerWidth < 768
            ? 'mobile'
            : 'desktop',
        funnel_stage: 'search' as const,
        is_one_way: !searchParams.returnDate,
        outbound_date: searchParams.departDate,
        return_date: searchParams.returnDate,
    }), [searchParams, arriveStationCount, departStationCount]);

    const tripsAtStation = useMemo(() => {
        if (!station) return 0;
        const key = `${station.side}Id` as keyof Trip;
        return trips.filter((trip) => trip[key] === station.id).length;
    }, [trips, station]);

    const isStationFiltered = station
        ? (station.side === 'depart'
            ? isDepartureStationsFiltered
            : isArrivalStationsFiltered)
        : false;

    const onPinClick = useCallback(
        (trip: Trip, side: MapMode, mapRef: React.RefObject<google.maps.Map | null>) => {
            const position = trip[`${side}LatLong`];
            const id = trip[`${side}Id`];
            const name = trip[`${side}Name`];
            const direction = side === 'depart'
                ? 'departure'
                : 'arrival';

            events.pinClicked(baseProperties, { id, name }, direction, side === 'depart');

            setStation({
                id, name, position, side,
            });
            smoothPanTo(mapRef.current, position);
        },
        [events, baseProperties],
    );

    const onFilterFromPopover = useCallback(() => {
        if (!station) return;

        const direction: 'departure' | 'arrival' = station.side === 'depart'
            ? 'departure'
            : 'arrival';

        if (isStationFiltered) {
            events.filterRemoved(baseProperties, station.id, direction, trips.length, station.side === 'depart');
            if (station.side === 'depart') {
                resetDepartureStations();
            } else {
                resetArrivalStations();
            }
        } else {
            events.filterApplied(baseProperties, station.id, direction, trips.length, station.side === 'depart');
            if (station.side === 'depart') {
                setDepartureStationOnlyActive(station.id);
            } else {
                setArrivalStationOnlyActive(station.id);
            }
        }
        setStation(undefined);
    }, [
        station, isStationFiltered, events, baseProperties, trips.length,
        resetDepartureStations, resetArrivalStations,
        setDepartureStationOnlyActive, setArrivalStationOnlyActive,
    ]);

    const onChipRemoveDeparture = useCallback(() => {
        events.chipRemoved(baseProperties, 'departure', trips.length);
        resetDepartureStations();
    }, [events, baseProperties, trips.length, resetDepartureStations]);

    const onChipRemoveArrival = useCallback(() => {
        events.chipRemoved(baseProperties, 'arrival', trips.length);
        resetArrivalStations();
    }, [events, baseProperties, trips.length, resetArrivalStations]);

    const onResetAll = useCallback(() => {
        const removed: string[] = [];
        if (isDepartureStationsFiltered) removed.push('departure_stations');
        if (isArrivalStationsFiltered) removed.push('arrival_stations');
        events.allFiltersCleared(baseProperties, removed, trips.length);
        resetDepartureStations();
        resetArrivalStations();
        setStation(undefined);
    }, [
        events, baseProperties, isDepartureStationsFiltered, isArrivalStationsFiltered,
        trips.length, resetDepartureStations, resetArrivalStations,
    ]);

    const clearStation = useCallback(() => {
        events.popoverDismissed(baseProperties);
        setStation(undefined);
    }, [events, baseProperties]);

    const onZoomIn = useCallback(
        () => events.zoomIn(baseProperties),
        [events, baseProperties],
    );

    const onZoomOut = useCallback(
        () => events.zoomOut(baseProperties),
        [events, baseProperties],
    );

    const mapReady = isLoaded && !loadError && !authFailed;

    if (!mapReady) return null;

    const showToolbar = hasFilters || !!onClose;

    return (
        <div className={styles.container}>
            {showToolbar && (
                <div className={styles.toolbar}>
                    {hasFilters && (
                        <StationFilterChips
                            className={styles.toolbarChips}
                            filteredCount={trips.length}
                            isArrivalFiltered={isArrivalStationsFiltered}
                            isDepartureFiltered={isDepartureStationsFiltered}
                            onResetAll={onResetAll}
                            onResetArrival={onChipRemoveArrival}
                            onResetDeparture={onChipRemoveDeparture}
                            totalCount={totalTripsUnfiltered ?? trips.length}
                        />
                    )}
                    {onClose && (
                        <button
                            aria-label="Hide map"
                            className={styles.closeButton}
                            onClick={onClose}
                            type="button"
                        >
                            &times;
                        </button>
                    )}
                </div>
            )}

            <div className={styles.splitMap}>
                <StationMapPane
                    center={departLocation}
                    isStationFiltered={isStationFiltered}
                    label={searchParams.originText?.split(',')[0] ?? ''}
                    labelMuted="Pickup in"
                    onClosePopover={clearStation}
                    onFilter={onFilterFromPopover}
                    onPinClick={onPinClick}
                    onZoomIn={onZoomIn}
                    onZoomOut={onZoomOut}
                    selectedStation={station}
                    side="depart"
                    trips={trips}
                    tripsAtStation={tripsAtStation}
                />

                <div className={styles.splitDivider} />

                <StationMapPane
                    center={arriveLocation}
                    isStationFiltered={isStationFiltered}
                    label={searchParams.destinationText?.split(',')[0] ?? ''}
                    labelMuted="Drop-off in"
                    onClosePopover={clearStation}
                    onFilter={onFilterFromPopover}
                    onPinClick={onPinClick}
                    onZoomIn={onZoomIn}
                    onZoomOut={onZoomOut}
                    selectedStation={station}
                    side="arrive"
                    trips={trips}
                    tripsAtStation={tripsAtStation}
                />
            </div>
        </div>
    );
}

export default React.memo(StationMapCore);
